import axios from "axios";
import moment from "moment";

import { config } from "../config";
import { CookieKey, getCookie, setCookie } from "../hooks/useCookie";
import GlobalErrorAlertService from "../modules/GlobalErrorAlert/services/global-error-alert.service";

const envConfig = config();
// Create a custom Axios instance with error handling
const axiosInstance = axios.create({
  baseURL: envConfig.apiUrl,
  withCredentials: true,
});

// Add a request interceptor to include the JWT token in requests
axiosInstance.interceptors.request.use(
  async (axiosConfig) => {
    const newAxiosConfig = axiosConfig;
    let token = getCookie(CookieKey.JWT_TOKEN);

    if (
      !token &&
      axiosConfig.withCredentials &&
      !axiosConfig.url?.includes("/auth/refresh-token")
    ) {
      const refreshToken = getCookie(CookieKey.REFRESH_TOKEN);
      if (refreshToken) {
        const refreshResponse: any = await axios.post(
          `${envConfig.apiUrl}/auth/refresh-token`,
          {
            refresh_token: refreshToken,
          },
          {
            withCredentials: true,
          },
        );

        // Update tokens
        setCookie(CookieKey.JWT_TOKEN, refreshResponse.data.jwt_token, {
          expires: moment()
            .add(refreshResponse.data.jwt_token_expires_in, "milliseconds")
            .toDate()
            .toUTCString(),
        });
        setCookie(CookieKey.REFRESH_TOKEN, refreshResponse.data.refresh_token, {
          expires: moment()
            .add(refreshResponse.data.refresh_token_expires_in, "milliseconds")
            .toDate()
            .toUTCString(),
        });

        token = refreshResponse.data.jwt_token;
      }
    }

    if (token && axiosConfig.withCredentials) {
      newAxiosConfig.headers.Authorization = `Bearer ${token}`;
    }
    return newAxiosConfig;
  },
  (error) => {
    return Promise.reject(error);
  },
);

// Add a response interceptor to handle errors globally
axiosInstance.interceptors.response.use(
  (response) => {
    return response;
  },
  async (error) => {
    if (error.response) {
      const originalRequest = error.config;
      const refreshToken = getCookie(CookieKey.REFRESH_TOKEN);

      if (error.response.status === 500) {
        GlobalErrorAlertService.showAlert(
          "An internal server error occurred. Please try again later.",
        );
      }

      if (error.response.status === 401 && !originalRequest.retry) {
        originalRequest.retry = true;

        if (!refreshToken) {
          return Promise.reject(error);
        }

        try {
          const response: any = await axios.post(
            `${envConfig.apiUrl}/auth/refresh-token`,
            {
              refresh_token: refreshToken,
            },
            {
              withCredentials: true,
            },
          );

          // Update tokens silently
          setCookie(CookieKey.JWT_TOKEN, response.data.jwt_token, {
            expires: moment()
              .add(response.data.jwt_token_expires_in, "milliseconds")
              .toDate()
              .toUTCString(),
          });
          setCookie(CookieKey.REFRESH_TOKEN, response.data.refresh_token, {
            expires: moment()
              .add(response.data.refresh_token_expires_in, "milliseconds")
              .toDate()
              .toUTCString(),
          });

          // Retry the original request with the new token
          originalRequest.headers.Authorization = `Bearer ${response?.data.jwt_token}`;
          return await axiosInstance(originalRequest);
        } catch (refreshError) {
          return Promise.reject(refreshError);
        }
      }
    }
    // Return a rejected promise with the error object, preserving status code
    const errorData = error.response?.data || {};
    const rejectionError = new Error(
      errorData.message || errorData.error || "Request failed",
    );
    Object.assign(rejectionError, {
      ...errorData,
      statusCode: error.response?.status,
      status: error.response?.status,
    });
    return Promise.reject(rejectionError);
  },
);

// Get function using Axios
const get = async (url: string, conf: any = {}) => {
  return axiosInstance.get(url, conf);
};

// Post function using Axios
const post = async (
  url: string,
  formData: Record<string, any>,
  withToken?: boolean,
) => {
  if (withToken) {
    return axiosInstance.post(url, formData);
  }
  return axiosInstance.post(url, formData);
};

const put = async (
  url: string,
  formData: any,
  options: Record<string, any>,
) => {
  return axiosInstance.put(url, formData, options);
};

const patch = async (url: string, formData: any) => {
  return axiosInstance.patch(url, formData);
};

const del = async (url: string) => {
  return axiosInstance.delete(url);
};

export default { post, get, put, patch, del };
